// NOSONAR
import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

const DatePicker = ({ value, onChange, label, required = false, disabled = false, mobileVerified = true }) => {
  // component state
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  // scroll refs — point directly at the scrollable element for reliability
  const dayScrollRef = useRef(null);
  const monthScrollRef = useRef(null);
  const yearScrollRef = useRef(null);

  // latest values for scroll handlers (avoid stale closures)
  const selectedDayRef = useRef(selectedDay);
  const selectedMonthRef = useRef(selectedMonth);
  const selectedYearRef = useRef(selectedYear);
  useEffect(() => { selectedDayRef.current = selectedDay; }, [selectedDay]);
  useEffect(() => { selectedMonthRef.current = selectedMonth; }, [selectedMonth]);
  useEffect(() => { selectedYearRef.current = selectedYear; }, [selectedYear]);

  // Helper to get days in a month/year
  const getDaysInMonth = (month, year) => {
    if (!month || !year) return 31;
    return new Date(parseInt(year, 10), parseInt(month, 10), 0).getDate();
  };

  // Compute days for selected month/year
  const React = require('react');
  const { useMemo } = React;
  const daysRaw = useMemo(() => {
    const daysCount = getDaysInMonth(selectedMonth, selectedYear);
    return Array.from({ length: daysCount }, (_, i) => String(i + 1).padStart(2, '0'));
  }, [selectedMonth, selectedYear]);
  const monthsRaw = [
    { value: '01', label: 'January' }, { value: '02', label: 'February' },
    { value: '03', label: 'March' }, { value: '04', label: 'April' },
    { value: '05', label: 'May' }, { value: '06', label: 'June' },
    { value: '07', label: 'July' }, { value: '08', label: 'August' },
    { value: '09', label: 'September' }, { value: '10', label: 'October' },
    { value: '11', label: 'November' }, { value: '12', label: 'December' }
  ];

  // age constraints (21 - 58 years)
  const today = new Date();
  const maxDate = new Date(today.getFullYear() - 21, today.getMonth(), today.getDate());
  const minDate = new Date(today.getFullYear() - 58, today.getMonth(), today.getDate());
  const warnMsg = `Date must make age between 21 and 58 years (${minDate.toLocaleDateString()} – ${maxDate.toLocaleDateString()})`;

  const currentYear = today.getFullYear();
  const maxAllowedYear = currentYear - 21;
  const minAllowedYear = currentYear - 58;
  // descending years (newer to older) — feels natural in a DOB picker
  const yearsRaw = Array.from({ length: maxAllowedYear - minAllowedYear + 1 }, (_, i) => String(maxAllowedYear - i));

  // buffered lists for scroll columns
  const [days, setDays] = useState([]);
  const [months, setMonths] = useState([]);
  const [years, setYears] = useState([]);
  const debounceTimers = useRef({ day: null, month: null, year: null });

  // initialize values from prop
  useEffect(() => {
    if (value) {
      const [y, m, d] = value.split('-');
      setSelectedDay(d || '');
      setSelectedMonth(m || '');
      setSelectedYear(y || '');
    } else {
      // On first open, do not select any value
      setSelectedDay('');
      setSelectedMonth('');
      setSelectedYear('');
    }
  }, [value]);

  // build buffered arrays when opening
  useEffect(() => {
    if (!isOpen) return;

    let bufferSize = 7; // default fallback
    const refs = [dayScrollRef, monthScrollRef, yearScrollRef];

    for (const r of refs) {
      if (r.current) {
        const sample = r.current.querySelector('.date-item[data-value]');
        if (sample) {
          const h = sample.offsetHeight;
          const H = r.current.offsetHeight;
          if (h && H) {
            bufferSize = Math.ceil(H / h / 2) + 3;
            break;
          }
        }
      }
    }

    const head = Array(bufferSize).fill('');
    const tail = Array(bufferSize).fill('');

    setDays([...head, ...daysRaw, ...tail]);
    setMonths([
      ...Array(bufferSize).fill(null).map(() => ({ value: '', label: '' })),
      ...monthsRaw,
      ...Array(bufferSize).fill(null).map(() => ({ value: '', label: '' }))
    ]);
    setYears([...head, ...yearsRaw, ...tail]);

    // after rendering, always center to the middle row of buffered arrays, do not select any value
    const t = setTimeout(() => {
      if (!isOpen) return;
      const centerDayIdx = Math.floor(days.length / 2);
      const centerMonthIdx = Math.floor(months.length / 2);
      const centerYearIdx = Math.floor(years.length / 2);
      scrollToCenter(dayScrollRef, days[centerDayIdx]);
      scrollToCenter(monthScrollRef, months[centerMonthIdx]?.value);
      scrollToCenter(yearScrollRef, years[centerYearIdx]);
      // Only reset selection if value is not set and picker is opening
      if (!value) {
        // Use null so nothing is selected or highlighted
        setSelectedDay(null);
        setSelectedMonth(null);
        setSelectedYear(null);
      }
    }, 120);

    return () => clearTimeout(t);
  }, [isOpen, days.length, months.length, years.length, value]);

  // eslint-disable-line react-hooks/exhaustive-deps

  // validation helpers (return true if disabled / invalid)
  const isYearDisabled = (year) => {
    if (!year) return true;
    const y = parseInt(year, 10);
    return y > maxAllowedYear || y < minAllowedYear;
  };

  const isMonthDisabled = (month, year) => {
    if (!month) return true;
    const m = parseInt(month, 10);
    if (!year) return false; // allow choosing month before year; final validation happens when both exist
    const y = parseInt(year, 10);
    if (isYearDisabled(year)) return true;
    if (y === maxAllowedYear && m > (maxDate.getMonth() + 1)) return true;
    if (y === minAllowedYear && m < (minDate.getMonth() + 1)) return true;
    return false;
  };

  const isDateDisabled = (day, month, year) => {
    if (!day || !month || !year) return true;
    const y = parseInt(year, 10), m = parseInt(month, 10), d = parseInt(day, 10);
    if (isYearDisabled(year)) return true;
    // check boundary days for edge years
    if (y === maxAllowedYear && m === (maxDate.getMonth() + 1) && d > maxDate.getDate()) return true;
    if (y === minAllowedYear && m === (minDate.getMonth() + 1) && d < minDate.getDate()) return true;
    const dt = new Date(y, m - 1, d);
    return dt < minDate || dt > maxDate;
  };

  // safe update: only pushes value out when day+month+year present
  const tryEmitDate = (d, m, y) => {
    if (!d || !m || !y) return;
    const daysInMonth = new Date(parseInt(y, 10), parseInt(m, 10), 0).getDate();
    const validDay = Math.min(parseInt(d, 10), daysInMonth);
    const formattedDay = String(validDay).padStart(2, '0');
    onChange(`${y}-${m}-${formattedDay}`);
  };

  // unified manual selection (click/keyboard) — shows toast only when a real invalid choice is made
  const handleSelect = (type, value) => {
    if (!value) return;

    if (type === 'year') {
      if (isYearDisabled(value)) {
        toast.warning(warnMsg);
        return;
      }
      setSelectedYear(value);
      // if month & day exist, re-emit/clamp
      if (selectedMonth) tryEmitDate(selectedDay, selectedMonth, value);
      return;
    }

    if (type === 'month') {
      // if year chosen, validate interplay; if not, allow selecting month and wait for year
      if (selectedYear && isMonthDisabled(value, selectedYear)) {
        toast.warning(warnMsg);
        return;
      }
      setSelectedMonth(value);
      if (selectedYear) tryEmitDate(selectedDay, value, selectedYear);
      return;
    }

    // day
    if (type === 'day') {
      // require month+year to validate day; if not present allow selection and wait
      if (selectedMonth && selectedYear && isDateDisabled(value, selectedMonth, selectedYear)) {
        toast.warning(warnMsg);
        return;
      }
      setSelectedDay(value);
      if (selectedMonth && selectedYear) tryEmitDate(value, selectedMonth, selectedYear);
      return;
    }
  };

  // keyboard opening handler
  const handleKeyDownOpen = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsOpen(true); }
  };

  // scroll helpers — ref is the actual scroll element
  const scrollToCenter = (scrollRef, value) => {
    if (!scrollRef?.current || !value) return;
    const scroller = scrollRef.current;
    const el = scroller.querySelector(`[data-value="${value}"]`);
    if (!el) return;
    scroller.scrollTop = el.offsetTop - (scroller.clientHeight / 2) + (el.offsetHeight / 2);
  };

  // parallax + auto-select; scrollerRef points to the .column-scroll element
  const applyParallaxEffect = (scrollerRef, type) => {
    if (!scrollerRef?.current) return;
    const scroller = scrollerRef.current;
    const items = Array.from(scroller.querySelectorAll('.date-item'));
    // filter items that actually have a data-value (buffers do not)
    const realItems = items.filter(it => {
      const v = it.getAttribute('data-value');
      return v !== null && v !== '';
    });
    if (!realItems.length) return;

    const center = scroller.scrollTop + scroller.clientHeight / 2;
    let closest = null, minDist = Infinity;

    realItems.forEach(item => {
      const itemCenter = item.offsetTop + item.offsetHeight / 2;
      const dist = Math.abs(itemCenter - center);
      // visual parallax transform
      const normalizedDistance = Math.min(dist / (scroller.clientHeight / 2), 1);
      if (item.classList.contains('selected')) {
        item.style.transform = 'scale(1.08) rotateX(0deg) translateZ(12px)';
        item.style.opacity = '1';
        item.style.filter = 'none';
      } else {
        const rotation = (itemCenter < center ? -1 : 1) * normalizedDistance * 30;
        const scale = Math.max(0.78, 1 - normalizedDistance * 0.25);
        const opacity = Math.max(0.35, 1 - normalizedDistance * 0.7);
        const blur = normalizedDistance * 2;
        const translateZ = -normalizedDistance * 40;
        item.style.transform = `scale(${scale}) rotateX(${rotation}deg) translateZ(${translateZ}px)`;
        item.style.opacity = opacity;
        item.style.filter = 'none';
      }

      if (dist < minDist) { minDist = dist; closest = item; }
    });

    if (!closest) return;
    const value = closest.getAttribute('data-value');

    if (debounceTimers.current[type]) clearTimeout(debounceTimers.current[type]);
    debounceTimers.current[type] = setTimeout(() => {
      // Use flexible validation: allow month/day selection before year, but validate if year/month exist
      if (type === 'year') {
        if (!isYearDisabled(value)) {
          setSelectedYear(value);
          if (selectedMonthRef.current && selectedDayRef.current) tryEmitDate(selectedDayRef.current, selectedMonthRef.current, value);
        }
      } else if (type === 'month') {
        if (!selectedYearRef.current || !isMonthDisabled(value, selectedYearRef.current)) {
          setSelectedMonth(value);
          if (selectedYearRef.current && selectedDayRef.current) tryEmitDate(selectedDayRef.current, value, selectedYearRef.current);
        }
      } else if (type === 'day') {
        if (!selectedMonthRef.current || !selectedYearRef.current) {
          // if month/year not set yet just set the day so the user can fine-tune later
          setSelectedDay(value);
        } else if (!isDateDisabled(value, selectedMonthRef.current, selectedYearRef.current)) {
          setSelectedDay(value);
          tryEmitDate(value, selectedMonthRef.current, selectedYearRef.current);
        }
      }
    }, 100);
  };

  // attach scroll listeners to actual scroll elements
  useEffect(() => {
    if (!isOpen) return;
    const cfg = [
      { ref: dayScrollRef, type: 'day' },
      { ref: monthScrollRef, type: 'month' },
      { ref: yearScrollRef, type: 'year' }
    ];

    const cleanups = [];

    cfg.forEach(({ ref, type }) => {
      const scroller = ref.current;
      if (!scroller) return;
      const onScroll = () => applyParallaxEffect(ref, type);
      scroller.addEventListener('scroll', onScroll, { passive: true });
      cleanups.push(() => scroller.removeEventListener('scroll', onScroll));
    });

    return () => cleanups.forEach(fn => fn && fn());
  }, [isOpen, days.length, months.length, years.length]); // rebind when lists populate

  // keep selected item centered whenever selection changes or lists change
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => {
      scrollToCenter(dayScrollRef, selectedDayRef.current);
      scrollToCenter(monthScrollRef, selectedMonthRef.current);
      scrollToCenter(yearScrollRef, selectedYearRef.current);
    }, 120);
    return () => clearTimeout(t);
  }, [isOpen, selectedDay, selectedMonth, selectedYear, days.length, months.length, years.length]);

  // click while disabled -> mobile verification toast
  const handleDisabledClick = () => {
    if (disabled && !mobileVerified && !value) {
      toast.warning('Please verify mobile number', { autoClose: 3000 });
    }
  };

  // render items; buffer items do not get data-value attribute (so they're ignored by selection logic)
  const renderColumnItems = (type, items, selected) => items.map((item, i) => {
    const value = type === 'month' ? (item?.value || '') : (item || '');
    const label = type === 'month' ? (item?.label || '') : (item || '');
  // Only real values can be selected, never select buffer/empty or null
  const isSelected = value && selected && value === selected;
    const disabled = value ? (
      type === 'day' ? isDateDisabled(value, selectedMonth, selectedYear)
        : type === 'month' ? isMonthDisabled(value, selectedYear)
          : isYearDisabled(value)
    ) : false;

    // only add data-value for real items (non-buffer)
    const dataValue = value ? value : undefined;

    return (
      <div
        key={`${type}-${i}`}
        className={`date-item${isSelected ? ' selected' : ''}${disabled ? ' disabled' : ''}`}
        data-value={dataValue}
        role="option"
        aria-selected={isSelected}
        tabIndex={isSelected ? 0 : -1}
        onClick={() => !disabled && value && handleSelect(type, value)}
        onKeyDown={(e) => !disabled && value && (e.key === 'Enter' || e.key === ' ') && handleSelect(type, value)}
      >
        {label || ' '}
      </div>
    );
  });

  // columns config (ref refers to the scroll element)
  const columns = [
    { type: 'day', ref: dayScrollRef, items: days, selected: selectedDay, aria: 'Days' },
    { type: 'month', ref: monthScrollRef, items: months, selected: selectedMonth, aria: 'Months' },
    { type: 'year', ref: yearScrollRef, items: years, selected: selectedYear, aria: 'Years' }
  ];

  const formatDisplayDate = () => {
    if (selectedDay && selectedMonth && selectedYear) {
      const monthName = monthsRaw.find(m => m.value === selectedMonth)?.label || '';
      return `${selectedDay} ${monthName} ${selectedYear}`;
    }
    return 'Select Date';
  };

  return (
    <>
      <div className="custom-date-picker">
        <div
          className={`form-control input-underline ps-0 custom-dob ${disabled ? 'disabled' : ''}`}
          onClick={disabled ? handleDisabledClick : () => setIsOpen(true)}
          onKeyDown={disabled ? (e) => (e.key === 'Enter' || e.key === ' ') && handleDisabledClick() : handleKeyDownOpen}
          tabIndex={disabled ? -1 : 0}
          role="button"
          aria-label="Open date picker"
          style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
        >
          <span className="fs-16">{formatDisplayDate()}</span>
          <i className="icon-timecalendar01 float-end"></i>
        </div>
      </div>

      {isOpen && (
        <div
          id="datepicker-dialog"
          role="dialog"
          aria-modal="true"
          className="datepicker-overlay"
          onClick={() => setIsOpen(false)}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsOpen(false);
            }
          }}
        >
          <div
            className="date-picker-modal"
            onClick={(e) => e.stopPropagation()}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
              }
            }}
          >
            <div className="date-picker-header">
              <h6>Select Date of Birth</h6>
              <button type="button" className="btn-close" onClick={() => setIsOpen(false)}></button>
            </div>

            <div className="datepicker-container" role="document" tabIndex={0}>
              <div className="columns-container">
                {columns.map(({ type, ref, items, selected, aria }) => (
                  <div className="column" key={type}>
                    {/* IMPORTANT: ref is attached to the scroll element for robust scroll handling */}
                    <div className="column-scroll" ref={ref} tabIndex={0} role="listbox" aria-label={aria}>
                      {renderColumnItems(type, items, selected)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="date-picker-footer">
              <button
                type="button"
                className="btn btn-primary w-100"
                onClick={() => {
                  // Only allow confirm if date is valid
                  if (!selectedDay || !selectedMonth || !selectedYear) {
                    toast.warning('Please select a valid date');
                    return;
                  }
                  if (isDateDisabled(selectedDay, selectedMonth, selectedYear)) {
                    const y = parseInt(selectedYear, 10), m = parseInt(selectedMonth, 10), d = parseInt(selectedDay, 10);
                    const dt = new Date(y, m - 1, d);
                    if (dt > maxDate) {
                      toast.warning('Age is less than 21');
                    } else if (dt < minDate) {
                      toast.warning('Age is greater than 58');
                    } else {
                      toast.warning('Invalid date');
                    }
                    return;
                  }
                  setIsOpen(false);
                }}
              >Confirm</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DatePicker;